import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface EventTooltipProps {
  title: string;
  children: React.ReactNode;
}

export default function EventTooltip({ title, children }: EventTooltipProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const targetRef = useRef<HTMLDivElement>(null);

  const updateTooltipPosition = () => {
    if (!targetRef.current) return;

    const rect = targetRef.current.getBoundingClientRect();
    
    // Simple positioning: place tooltip centered above the target element
    const x = rect.left + rect.width / 2;
    const y = rect.top;
    
    setTooltipStyle({
      position: 'fixed',
      left: `${x}px`,
      top: `${y}px`,
      transform: 'translateX(-50%) translateY(-100%)',
      marginTop: '-8px',
      zIndex: 9999,
      pointerEvents: 'none',
      backgroundColor: '#1f2937',
      color: 'white',
      padding: '8px 12px',
      borderRadius: '8px',
      fontSize: '0.875rem',
      maxWidth: '300px',
      whiteSpace: 'pre-wrap',
      wordWrap: 'break-word',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    });
  };

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  // Update tooltip position when shown or when scrolling/resizing
  useEffect(() => {
    if (!showTooltip) return;

    updateTooltipPosition();

    const handleScroll = () => updateTooltipPosition();
    const handleResize = () => updateTooltipPosition();

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [showTooltip]);

  return (
    <>
      <div
        ref={targetRef}
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      
      {showTooltip && createPortal(
        <div style={tooltipStyle}>
          {title}
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: '8px',
              height: '8px',
              backgroundColor: '#1f2937',
            }}
          />
        </div>,
        document.body
      )}
    </>
  );
}
